import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../styles/globals';

const ChatbotWrapper = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;

  @media (max-width: 768px) {
    bottom: 10px;
    right: 10px;
  }
`;

const ChatbotButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  box-shadow: 0 8px 25px rgba(243, 156, 107, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(243, 156, 107, 0.4);
  }
`;

const ChatbotContainer = styled.div`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 400px;
  height: 600px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  background: white;
  z-index: 999;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 90vw;
    height: 70vh;
    right: 5vw;
    bottom: 80px;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.darkBackground} 100%);
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${fonts.inter};
  min-height: 60px;
`;

const HeaderText = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
`;

const HeaderTitle = styled.div`
  font-family: ${fonts.playfair};
  font-size: 1.1rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ChatBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  overflow: hidden;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Message = styled.div`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  font-family: ${fonts.inter};
  font-size: 14px;
  line-height: 1.4;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  background: ${props => props.isUser 
    ? `linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%)` 
    : 'white'
  };
  color: ${props => props.isUser ? 'white' : colors.darkBackground};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: pre-wrap;
`;

const InputContainer = styled.div`
  padding: 20px;
  background: white;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 25px;
  font-family: ${fonts.inter};
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: ${colors.accent};
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const QuickActionButton = styled.button`
  padding: 8px 16px;
  background: white;
  border: 2px solid ${colors.accent};
  border-radius: 20px;
  color: ${colors.accent};
  font-family: ${fonts.inter};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.accent};
    color: white;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  margin: 10px 0;
  border: 1px solid #fcc;
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const TypingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.accent};
  animation: typing 1.4s infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
`;

// Dify API configuration
const DIFY_API_URL = 'https://chatbot.elevaiti.com/v1';
const DIFY_TOKEN = 'app-oxHRySb8xhws8hUJr39vMyjd';

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState('');
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: '1',
        text: '¡Hola! Soy Gaochbot, tu asistente virtual. ¿En qué puedo ayudarte hoy?',
        isUser: false,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length]);

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  const sendMessageToDify = async (message) => {
    try {
      const response = await fetch(`${DIFY_API_URL}/chat-messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${DIFY_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inputs: {},
          query: message,
          response_mode: 'blocking',
          conversation_id: conversationId || undefined,
          user: 'user'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Store conversation ID for future messages
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }

      return data.answer;
    } catch (error) {
      console.error('Error sending message to Dify:', error);
      throw error;
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setError('');

    try {
      const botResponse = await sendMessageToDify(inputValue.trim());
      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setError('Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.');
      console.error('Chatbot error:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickAction = async (action) => {
    const actionMessage = {
      id: Date.now().toString(),
      text: action,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, actionMessage]);
    setIsTyping(true);
    setError('');

    try {
      const botResponse = await sendMessageToDify(action);
      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setError('Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.');
      console.error('Chatbot error:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <ChatbotWrapper>
      <ChatbotButton onClick={toggleChatbot} aria-label="Abrir chat">
        💬
      </ChatbotButton>

      {isOpen && (
        <ChatbotContainer>
          <ChatHeader>
            <HeaderText>
              <HeaderIcon>E</HeaderIcon>
              <HeaderTitle>Chat con Gaoch bot</HeaderTitle>
            </HeaderText>
            <CloseButton onClick={toggleChatbot} aria-label="Cerrar chat">
              ×
            </CloseButton>
          </ChatHeader>
          
          <ChatBody>
            <MessagesContainer>
              {messages.map((message) => (
                <Message key={message.id} isUser={message.isUser}>
                  {message.text}
                </Message>
              ))}
              
              {error && (
                <ErrorMessage>
                  {error}
                </ErrorMessage>
              )}
              
              {isTyping && (
                <Message isUser={false}>
                  <TypingIndicator>
                    <TypingDot delay="0s" />
                    <TypingDot delay="0.2s" />
                    <TypingDot delay="0.4s" />
                  </TypingIndicator>
                </Message>
              )}
              
              {messages.length === 1 && (
                <QuickActions>
                  <QuickActionButton onClick={() => handleQuickAction('¿Cuáles son tus servicios de coaching conversacional?')}>
                    Servicios
                  </QuickActionButton>
                  <QuickActionButton onClick={() => handleQuickAction('¿Cuánto cuestan las sesiones?')}>
                    Precios
                  </QuickActionButton>
                  <QuickActionButton onClick={() => handleQuickAction('Quiero agendar una sesión')}>
                    Agendar
                  </QuickActionButton>
                </QuickActions>
              )}
              
              <div ref={messagesEndRef} />
            </MessagesContainer>
            
            <InputContainer>
              <MessageInput
                type="text"
                placeholder="Escribe tu mensaje..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping}
              />
              <SendButton 
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                aria-label="Enviar mensaje"
              >
                ➤
              </SendButton>
            </InputContainer>
          </ChatBody>
        </ChatbotContainer>
      )}
      
      <style jsx>{`
        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }
      `}</style>
    </ChatbotWrapper>
  );
}

export default Chatbot; 